import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Badge, Dropdown, Menu, Tooltip } from 'antd';
import { BellOutlined, LogoutOutlined, RedoOutlined } from '@ant-design/icons';
import AppContext from '../../contexts/AppContext';
import { USER_STATES } from '../../utils/constants';
import type { EUserState } from '../../utils/constants';
import { setUser as setAnalyticsUser } from '../../utils/analytics';
import api from '../../utils/api';
import { useCampaignList } from '../../services/campaignManager';
import { useMoopShell } from './MoopShellContext';
import { useOnboardingTour } from '../onboarding-tour';
import styles from './MoopHeader.module.scss';

const getInitials = (firstName?: string, lastName?: string, email?: string): string => {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName[0].toUpperCase();
  if (email) return email[0].toUpperCase();
  return 'U';
};

interface AppUser {
  firstName?: string;
  lastName?: string;
  email?: string;
}

// Stable params object for the header's pending-review count query.
// Module-level constant avoids recreating the object on every render.
const PENDING_REVIEW_PARAMS = { limit: 100 } as const;

const MoopHeader: React.FC = () => {
  const { user, setUser, resetFilterValues } = useContext(AppContext);
  const { sidebarCollapsed } = useMoopShell();
  const history = useHistory();

  const userData = user && typeof user === 'object' ? (user as AppUser) : null;
  const userName = userData
    ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email || 'User'
    : 'User';
  const userInitials = userData ? getInitials(userData.firstName, userData.lastName, userData.email) : 'U';
  // TODO(MIC-2875 engineering): backend init-info needs to surface merchant display name
  const merchantName = 'Acme Commerce';

  // Pending review count for notification badge — lightweight call with
  // high limit to capture all pending items. Shared constant avoids
  // recreating the params object on every render.
  const { retake } = useOnboardingTour();
  const { data: headerCampaigns } = useCampaignList(PENDING_REVIEW_PARAMS);
  const pendingReviewCount = useMemo(
    () => headerCampaigns.filter((o) => o.status === 'REJECTED' || o.status === 'PENDING_APPROVAL').length,
    [headerCampaigns]
  );

  const handleNotificationClick = () => {
    // When already on /campaigns, replace instead of push so the tab query param updates
    if (history.location.pathname === '/campaigns') {
      history.replace('/campaigns?tab=pending-review');
    } else {
      history.push('/campaigns?tab=pending-review');
    }
  };

  const handleLogout = async (): Promise<void> => {
    await api.logout();
    setAnalyticsUser(null);
    (setUser as (state: EUserState) => void)(USER_STATES.NOT_AUTHENTICATED);
    resetFilterValues();
    history.push('/signin');
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="retake-tour" icon={<RedoOutlined />} onClick={retake}>
        Retake tour
      </Menu.Item>
      <Menu.Item key="logout" danger icon={<LogoutOutlined />} onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <header
      className={styles.header}
      style={{ left: sidebarCollapsed ? 'var(--sbds-sidebar-width-collapsed)' : 'var(--sbds-sidebar-width-expanded)' }}
    >
      <div className={styles.left}>
        {/* Static merchant label — was previously rendered as a
            switcher with a caret + hover state, but the backend
            init-info only exposes a single merchant for now, so the
            switching affordance was a lie. Drop the caret + hover to
            read as a read-only header brand instead. Re-introduce the
            dropdown once `init-info.brandsAndOutlets` ships multi-
            merchant data (see TODO above on `merchantName`). */}
        <div className={styles.workspaceLabel}>
          <span className={styles.workspaceAvatar}>{merchantName[0].toUpperCase()}</span>
          <span className={styles.workspaceName}>{merchantName}</span>
        </div>
      </div>

      <div className={styles.right}>
        <Tooltip title="Notifications">
          <button
            className={`${styles.iconBtn} ${styles.bellBtn}`}
            type="button"
            aria-label="Notifications"
            onClick={handleNotificationClick}
          >
            <Badge dot={pendingReviewCount > 0} offset={[-2, 2]}>
              <BellOutlined style={{ fontSize: 18 }} />
            </Badge>
          </button>
        </Tooltip>

        <div className={styles.separator} />

        <Dropdown overlay={userMenu} trigger={['click']} placement="bottomRight">
          <button className={styles.userBtn} type="button" aria-label="User menu">
            <span className={styles.userAvatar}>{userInitials}</span>
            <span className={styles.userName}>{userName}</span>
          </button>
        </Dropdown>
      </div>
    </header>
  );
};

export default MoopHeader;
